import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  X, Gift, Bell, AlertTriangle, Trophy, Settings, 
  ChevronRight, Megaphone
} from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAG_CONFIG = {
  promotion: { color: 'from-green-600 to-emerald-600', icon: Gift, textColor: 'text-green-400' },
  update: { color: 'from-blue-600 to-cyan-600', icon: Bell, textColor: 'text-blue-400' },
  alert: { color: 'from-red-600 to-orange-600', icon: AlertTriangle, textColor: 'text-red-400' },
  tournament: { color: 'from-purple-600 to-pink-600', icon: Trophy, textColor: 'text-purple-400' },
  system_notice: { color: 'from-yellow-600 to-amber-600', icon: Settings, textColor: 'text-yellow-400' }
};

// Homepage Banner Component
export const AnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await axios.get(`${API}/announcements/active?location=homepage`);
        setAnnouncements(response.data || []);
      } catch (error) {
        console.error('Failed to fetch announcements');
      }
    };
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    if (announcements.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % announcements.length);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [announcements.length]);

  if (dismissed || announcements.length === 0) return null;

  const current = announcements[currentIndex];
  const TagIcon = TAG_CONFIG[current.tag]?.icon || Megaphone;
  const gradientColor = TAG_CONFIG[current.tag]?.color || 'from-[#00FF94] to-[#00F0FF]';

  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`relative bg-gradient-to-r ${gradientColor} text-white py-3 px-4`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <TagIcon className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-semibold mr-2">{current.title}</span>
            <span className="text-white/90 text-sm hidden sm:inline">
              {current.message.length > 80 ? current.message.substring(0, 80) + '...' : current.message}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {current.cta_text && current.cta_link && (
            <Button
              size="sm"
              onClick={() => navigate(current.cta_link)}
              className="bg-white/20 hover:bg-white/30 text-white text-sm"
            >
              {current.cta_text}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          
          {announcements.length > 1 && (
            <div className="flex gap-1 mx-2">
              {announcements.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentIndex ? 'bg-white' : 'bg-white/40'
                  }`}
                />
              ))}
            </div>
          )}
          
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-white/20 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Dashboard Panel Component
export const AnnouncementPanel = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await axios.get(`${API}/announcements/active?location=dashboard`);
        setAnnouncements(response.data || []);
      } catch (error) {
        console.error('Failed to fetch announcements');
      }
    };
    fetchAnnouncements();
  }, []);

  if (announcements.length === 0) return null;

  return (
    <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden" data-testid="announcement-panel">
      <div className="px-4 py-3 bg-white/5 border-b border-white/10 flex items-center gap-2">
        <Megaphone className="w-5 h-5 text-[#00FF94]" />
        <h3 className="font-semibold">Announcements</h3>
        <span className="px-2 py-0.5 rounded-full bg-[#00FF94]/20 text-[#00FF94] text-xs">
          {announcements.length}
        </span>
      </div>
      
      <div className="divide-y divide-white/5">
        {announcements.slice(0, 5).map((announcement) => {
          const TagIcon = TAG_CONFIG[announcement.tag]?.icon || Bell;
          const textColor = TAG_CONFIG[announcement.tag]?.textColor || 'text-white/70';
          
          return (
            <motion.div
              key={announcement.announcement_id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 hover:bg-white/5 transition-colors cursor-pointer"
              onClick={() => setExpanded(expanded === announcement.announcement_id ? null : announcement.announcement_id)}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg bg-white/10 ${textColor}`}>
                  <TagIcon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {announcement.priority === 'pinned' && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-[#00FF94]/20 text-[#00FF94]">
                        PINNED
                      </span>
                    )}
                    <h4 className="font-medium text-sm truncate">{announcement.title}</h4>
                  </div>
                  
                  <AnimatePresence>
                    {expanded === announcement.announcement_id ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        <p className="text-white/60 text-sm whitespace-pre-line mb-3">
                          {announcement.message}
                        </p>
                        {announcement.cta_text && announcement.cta_link && (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(announcement.cta_link);
                            }}
                            className="btn-primary text-xs"
                          >
                            {announcement.cta_text}
                            <ChevronRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                      </motion.div>
                    ) : (
                      <p className="text-white/50 text-xs truncate">
                        {announcement.message.substring(0, 60)}...
                      </p>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// Lobby Sidebar Component
export const AnnouncementSidebar = () => {
  const [announcements, setAnnouncements] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await axios.get(`${API}/announcements/active?location=lobby`);
        setAnnouncements(response.data || []);
      } catch (error) {
        console.error('Failed to fetch announcements');
      }
    };
    fetchAnnouncements();
  }, []);

  if (announcements.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="announcement-sidebar">
      {announcements.slice(0, 3).map((announcement) => {
        const TagIcon = TAG_CONFIG[announcement.tag]?.icon || Bell;
        const gradientColor = TAG_CONFIG[announcement.tag]?.color || 'from-[#00FF94]/20 to-[#00F0FF]/20';
        
        return (
          <motion.div
            key={announcement.announcement_id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className={`p-4 rounded-xl bg-gradient-to-br ${gradientColor} border border-white/10`}
          >
            <div className="flex items-center gap-2 mb-2">
              <TagIcon className="w-4 h-4 text-white/80" />
              <span className="text-xs uppercase tracking-wider text-white/60">
                {announcement.tag.replace('_', ' ')}
              </span>
            </div>
            <h4 className="font-semibold text-sm mb-1">{announcement.title}</h4>
            <p className="text-white/60 text-xs line-clamp-2 mb-3">{announcement.message}</p>
            
            {announcement.cta_text && announcement.cta_link && (
              <Button
                size="sm"
                onClick={() => navigate(announcement.cta_link)}
                className="w-full bg-white/10 hover:bg-white/20 text-white text-xs"
              >
                {announcement.cta_text}
              </Button>
            )}
          </motion.div>
        );
      })}
    </div>
  );
};

// Popup Modal Component
export const AnnouncementPopup = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPopup = async () => {
      // Check if already shown this session
      const shownPopups = JSON.parse(sessionStorage.getItem('shownAnnouncements') || '[]');
      
      try {
        const response = await axios.get(`${API}/announcements/popup`);
        const popups = response.data || [];
        
        // Find first popup not shown this session
        const unshown = popups.find(p => !shownPopups.includes(p.announcement_id));
        
        if (unshown) {
          setAnnouncement(unshown);
          setVisible(true);
          // Mark as shown
          sessionStorage.setItem('shownAnnouncements', JSON.stringify([...shownPopups, unshown.announcement_id]));
        }
      } catch (error) {
        console.error('Failed to fetch popup announcements');
      }
    };
    
    // Delay popup by 2 seconds after page load
    const timer = setTimeout(fetchPopup, 2000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible || !announcement) return null;

  const TagIcon = TAG_CONFIG[announcement.tag]?.icon || Megaphone;
  const gradientColor = TAG_CONFIG[announcement.tag]?.color || 'from-[#00FF94] to-[#00F0FF]';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={() => setVisible(false)}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-[#0A0A0B] rounded-2xl border border-white/20 overflow-hidden"
        >
          {/* Header */}
          <div className={`bg-gradient-to-r ${gradientColor} p-6 text-center`}>
            <div className="w-16 h-16 mx-auto bg-white/20 rounded-2xl flex items-center justify-center mb-4">
              <TagIcon className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">{announcement.title}</h2>
          </div>
          
          {/* Content */}
          <div className="p-6">
            <p className="text-white/70 text-center whitespace-pre-line mb-6">
              {announcement.message}
            </p>
            
            <div className="flex flex-col gap-3">
              {announcement.cta_text && announcement.cta_link && (
                <Button
                  onClick={() => {
                    setVisible(false);
                    navigate(announcement.cta_link);
                  }}
                  className="btn-primary w-full"
                >
                  {announcement.cta_text}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setVisible(false)}
                className="btn-secondary w-full"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default {
  AnnouncementBanner,
  AnnouncementPanel,
  AnnouncementSidebar,
  AnnouncementPopup
};
